import { object, string, number, array, lazy, mixed } from 'yup';

const URLSchema = object().shape({
  url: object().shape({
    operator: string()
      .oneOf(['==', '!=', '*', '!*', '^', '!^', '$', '!$', 'regex'])
      .required(),
    value: string().when('operator', {
      is: op => ['==', '!=', 'regex'].includes(op),
      then: string().notRequired(),
      otherwise: string().required(),
    }),
  }),
});

const DomainsSchema = object().shape({
  domains: object().shape({
    operator: string().matches(/==/).required(),
    value: string().required(),
  }),
});

const PropertiesInnerSchema = object().shape({
  property: string().required(),
  operator: string().oneOf([
    '==',
    '!=',
    '>',
    '<',
    '*',
    '!*',
    '^',
    '!^',
    '$',
    '!$',
    '?',
    '!?',
    'in',
    'not in',
    'since',
    '> ago',
    '< ago',
    'within',
    '> time',
    '< time',
    'regex',
  ]),
  value: string().when('operator', {
    is: op => ['?', '!?', '==', '!=', 'regex'].includes(op),
    then: string().notRequired(),
    otherwise: string().when('operator', {
      is: op => ['> ago', '< ago', 'within', '> time', '< time'].includes(op),
      then: string().matches(/\d+/).required(),
      otherwise: string().required(),
    }),
  }),
});

const PropertiesSchema = object().shape({
  properties: PropertiesInnerSchema,
});

const GroupSchema = object().shape({
  group: PropertiesInnerSchema,
});

const LanguagesSchema = object().shape({
  languages: object().shape({
    value: string().min(2).max(5).required(),
    operator: string().matches(/\^/).required(),
  }),
});

const ContentSchema = object().shape({
  content: object().shape({
    content: string().required(),
    operator: string().oneOf(['< ago', 'since']),
    times: number(),
    value: number().when('operator', {
      is: op => ['< ago', 'since'].includes(op),
      then: number().required(),
    }),
  }),
});

const EventSchema = object().shape({
  event: object().shape({
    event: string().required(),
    times: number(),
    operator: string().oneOf(['< ago', 'since']),
    value: number().when('operator', {
      is: op => ['< ago', 'since'].includes(op),
      then: number().required(),
      otherwise: number().notRequired(),
    }),
  }),
});

const SegmentsSchema = object().shape({
  segments: object().shape({
    segment: string().required(),
  }),
});

const ChecklistsSchema = object().shape({
  checklists: object().shape({
    checklist: string().required(),
    status: string().oneOf(['in_progress', 'skipped', 'completed\ndismissed']),
  }),
});

const AttributesSchema = object().shape({
  attributes: object().shape({
    attribute: string().required(),
    operator: string().oneOf([
      '==',
      '!=',
      '*',
      '!*',
      '^',
      '!^',
      '$',
      '!$',
      '?',
      '!?',
      'in',
      'not in',
      '>',
      '<',
      'regex',
    ]),
    value: string().required(),
  }),
});

// the final group of schemas must be defined dynamically since they
// allow recursive conditions.

let AndSchema;
let OrSchema;
let NotSchema;
let TriggerSchema;

const ConditionsSchema = mixed().test({
  name: 'is-known-clause-type',
  test: value => {
    const [key] = Object.keys(value || {});

    const knownSchemas = {
      and: AndSchema,
      or: OrSchema,
      not: NotSchema,
      url: URLSchema,
      domains: DomainsSchema,
      properties: PropertiesSchema,
      group: GroupSchema,
      languages: LanguagesSchema,
      content: ContentSchema,
      event: EventSchema,
      segments: SegmentsSchema,
      checklists: ChecklistsSchema,
      trigger: TriggerSchema,
      attributes: AttributesSchema,
    };

    if (knownSchemas[key]) {
      return knownSchemas[key].validateSync(value);
    }

    return false;
  },
  // eslint-disable-next-line no-template-curly-in-string
  message: '${path} is not a recognized clause',
});

TriggerSchema = object().shape({
  trigger: object().shape({
    event: string().required(),
    conditions: lazy(conditions =>
      conditions ? ConditionsSchema : mixed().notRequired()
    ),
  }),
});

AndSchema = object().shape({
  and: array().of(ConditionsSchema).required(),
});

OrSchema = object().shape({
  or: array().of(ConditionsSchema).required(),
});

NotSchema = object().shape({
  not: array().of(ConditionsSchema).required(),
});

export default ConditionsSchema;
